import { FormEvent, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  Building2,
  Mail,
  Palette,
  UserPlus,
  Wrench,
} from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { AUTOMATIK_FELDER } from "../../lib/automatik";
import { Timer } from "lucide-react";

interface GewerkOption {
  id: string;
  label: string;
  kurz: string;
  subServices: string[];
}

export function NewCompany() {
  const navigate = useNavigate();
  const [gewerkOptions, setGewerkOptions] = useState<GewerkOption[]>([]);
  const [gewerke, setGewerke] = useState<string[]>([]);

  useEffect(() => {
    api<{ items: GewerkOption[] }>("/master/gewerke")
      .then((r) => setGewerkOptions(r.items))
      .catch(() => setGewerkOptions([]));
  }, []);

  function toggleGewerk(id: string) {
    setGewerke((g) => (g.includes(id) ? g.filter((x) => x !== id) : [...g, id]));
  }
  const [form, setForm] = useState({
    name: "",
    slug: "",
    phone: "",
    email: "",
    address: "",
    website: "",
    primaryColor: "#1a1a2e",
    greetingStyle: "sie" as "sie" | "du",
    trackingPageUrl: "",
    emailSignature: "",
    emailSenderName: "",
    notificationEmail: "",
    userName: "",
    userEmail: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [automatik, setAutomatik] = useState<Record<string, number>>(
    Object.fromEntries(AUTOMATIK_FELDER.map((f) => [f.key, f.standard])),
  );

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api("/master/companies", {
        method: "POST",
        body: {
          name: form.name,
          slug: form.slug,
          phone: form.phone || null,
          email: form.email || null,
          address: form.address || null,
          website: form.website || null,
          primaryColor: form.primaryColor,
          greetingStyle: form.greetingStyle,
          trackingPageUrl: form.trackingPageUrl || null,
          gewerke: gewerke.length > 0 ? gewerke : undefined,
          emailSignature: form.emailSignature || null,
          emailSenderName: form.emailSenderName || null,
          notificationEmail: form.notificationEmail || null,
          automatik,
          firstUser: {
            name: form.userName,
            email: form.userEmail,
            role: "admin",
          },
        },
      });
      navigate("/master");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Fehler beim Anlegen");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={submit} className="mx-auto max-w-3xl space-y-5">
      <Link
        to="/master"
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
      >
        <ArrowLeft className="h-4 w-4" /> Unternehmen
      </Link>
      <h1 className="page-title">Neues Unternehmen</h1>

      <section className="card">
        <h2 className="section-title mb-4">
          <Building2 className="h-3.5 w-3.5 text-slate-400" /> Firma
        </h2>
        <div className="space-y-3">
          <Row label="Name">
            <input
              className="input"
              value={form.name}
              onChange={(e) => {
                update("name", e.target.value);
                if (!form.slug) update("slug", slugify(e.target.value));
              }}
              required
            />
          </Row>
          <Row label="Slug (URL)">
            <input
              className="input font-mono"
              value={form.slug}
              onChange={(e) => update("slug", e.target.value)}
              required
              pattern="[a-z0-9][a-z0-9-]{1,62}[a-z0-9]"
            />
          </Row>
          <Row label="Telefon">
            <input
              className="input"
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
          </Row>
          <Row label="E-Mail">
            <input
              className="input"
              type="email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
            />
          </Row>
          <Row label="Adresse">
            <input
              className="input"
              value={form.address}
              onChange={(e) => update("address", e.target.value)}
            />
          </Row>
          <Row label="Tracking-Seite">
            <input
              className="input"
              placeholder="https://kunde.de/kontakt"
              value={form.trackingPageUrl}
              onChange={(e) => update("trackingPageUrl", e.target.value)}
            />
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Wrench className="h-3.5 w-3.5 text-slate-400" /> Gewerke
        </h2>
        <p className="mb-3 text-sm text-slate-500">
          Welche Gewerke deckt der Betrieb ab? Das Widget zeigt Kunden dann
          genau die passenden Leistungen. Bei nur einem Gewerk entfällt die
          Bereichs-Auswahl komplett — Kunden wählen direkt die konkrete
          Leistung. („Etwas anderes“ als Freitext-Option ist immer dabei.)
        </p>
        <div className="flex flex-wrap gap-2">
          {gewerkOptions.map((g) => {
            const active = gewerke.includes(g.id);
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => toggleGewerk(g.id)}
                title={g.subServices.join(" · ")}
                className={
                  "rounded-full border px-4 py-1.5 text-sm font-medium transition " +
                  (active
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-400")
                }
              >
                {g.kurz}
              </button>
            );
          })}
        </div>
        {gewerke.length > 0 && (
          <div className="mt-3 space-y-1 text-xs text-slate-500">
            {gewerkOptions
              .filter((g) => gewerke.includes(g.id))
              .map((g) => (
                <p key={g.id}>
                  <span className="font-medium text-slate-700">{g.label}:</span>{" "}
                  {g.subServices.join(" · ")}
                </p>
              ))}
          </div>
        )}
        {gewerke.length === 0 && (
          <p className="mt-3 text-xs text-amber-600">
            Ohne Auswahl werden alle Gewerke eingerichtet.
          </p>
        )}
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Palette className="h-3.5 w-3.5 text-slate-400" /> Branding
        </h2>
        <div className="space-y-3">
          <Row label="Primärfarbe">
            <div className="flex items-center gap-3">
              <input
                type="color"
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200"
                value={form.primaryColor}
                onChange={(e) => update("primaryColor", e.target.value)}
              />
              <span className="font-mono text-sm text-slate-600">
                {form.primaryColor}
              </span>
            </div>
          </Row>
          <Row label="Anrede">
            <select
              className="input"
              value={form.greetingStyle}
              onChange={(e) =>
                update("greetingStyle", e.target.value as "sie" | "du")
              }
            >
              <option value="sie">Sie</option>
              <option value="du">Du</option>
            </select>
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Mail className="h-3.5 w-3.5 text-slate-400" /> E-Mail
        </h2>
        <div className="space-y-3">
          <Row label="Absendername">
            <input
              className="input"
              value={form.emailSenderName}
              onChange={(e) => update("emailSenderName", e.target.value)}
            />
          </Row>
          <Row label="Benachrichtigung an">
            <input
              className="input"
              type="email"
              value={form.notificationEmail}
              onChange={(e) => update("notificationEmail", e.target.value)}
            />
          </Row>
          <Row label="Signatur">
            <textarea
              className="input h-24"
              value={form.emailSignature}
              onChange={(e) => update("emailSignature", e.target.value)}
            />
          </Row>
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <Timer className="h-3.5 w-3.5 text-slate-400" /> Automatik
        </h2>
        <p className="mb-3 text-sm text-slate-500">
          Zeitschwellen der automatischen Erinnerungen — der Betrieb kann sie
          später selbst in seinen Einstellungen ändern. 0 = aus.
        </p>
        <div className="space-y-3">
          {AUTOMATIK_FELDER.map((f) => (
            <Row key={f.key} label={f.label}>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  className="input w-28"
                  value={automatik[f.key] ?? f.standard}
                  onChange={(e) =>
                    setAutomatik((v) => ({ ...v, [f.key]: Math.max(0, Number(e.target.value) || 0) }))
                  }
                />
                <span className="text-sm text-slate-500">{f.einheit}</span>
              </div>
              <p className="mt-1 px-1 text-xs text-slate-500">{f.hinweis}</p>
            </Row>
          ))}
        </div>
      </section>

      <section className="card">
        <h2 className="section-title mb-4">
          <UserPlus className="h-3.5 w-3.5 text-slate-400" /> Erster Admin-User
        </h2>
        <div className="space-y-3">
          <Row label="Name">
            <input
              className="input"
              value={form.userName}
              onChange={(e) => update("userName", e.target.value)}
              required
            />
          </Row>
          <Row label="E-Mail">
            <input
              className="input"
              type="email"
              value={form.userEmail}
              onChange={(e) => update("userEmail", e.target.value)}
              required
            />
          </Row>
          <p className="px-1 text-xs text-slate-500">
            Kein Passwort nötig — die Anmeldung läuft über einen Link, den wir
            per E-Mail an diese Adresse schicken.
          </p>
        </div>
      </section>

      {error && (
        <p className="inline-flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </p>
      )}

      <div className="flex justify-end">
        <button type="submit" className="btn-primary" disabled={submitting}>
          Anlegen
        </button>
      </div>
    </form>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid gap-2 sm:grid-cols-[180px_1fr] sm:items-center">
      <span className="text-sm text-slate-600">{label}</span>
      <div>{children}</div>
    </label>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}
